import logError from 'src/data/services/logError';
import * as Font from 'expo-font';
import { useState, useEffect, useRef } from 'react';
import { Platform, BackHandler } from 'react-native';
import errorHandler from 'src/core/utils/errorHandler';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { navigationRef } from 'src/domain/stores/useNavigationStore';

function useCachedResources() {
	const [resourcesLoaded, setResourcesLoaded] = useState(false);
	const messageIdRef = useRef<string | null>(null);

	const { init: initServicesStore, initNotifications } = useServicesStore();
	const { getNotificationsByToken, onForegroundMessage } = useNotificationStore();

	useEffect(() => {
		loadResourcesAndDataAsync().catch(logError);
	}, []);

	async function loadResourcesAndDataAsync() {
		try {
			initServicesStore();

			// Restore the persisted auth session (token + profile) before the UI renders.
			await useAuthenticationStore.getState().init();

			await Font.loadAsync({
				[Fonts.montserratBold]: require('assets/fonts/Montserrat/Montserrat-Bold.ttf'),
				[Fonts.montserratMedium]: require('assets/fonts/Montserrat/Montserrat-Medium.ttf'),
				[Fonts.montserratRegular]: require('assets/fonts/Montserrat/Montserrat-Regular.ttf'),
				[Fonts.montserratLight]: require('assets/fonts/Montserrat/Montserrat-Light.ttf'),
				[Fonts.montserratLightItalic]: require('assets/fonts/Montserrat/Montserrat-LightItalic.ttf'),
				...(Platform.OS === 'android' && {
					Roboto: require('assets/fonts/Roboto.ttf'),
					Roboto_medium: require('assets/fonts/Roboto_medium.ttf'),
				}),
			});

			if (Platform.OS === 'android') {
				BackHandler.addEventListener('hardwareBackPress', () => {
					const routeName = navigationRef.current?.getCurrentRoute()?.name;
					if (!['MainTab', 'Login'].includes(routeName ?? '') && navigationRef.current?.canGoBack()) {
						navigationRef.current?.goBack();
						return true;
					}
					return false;
				});
			}

			await initNotifications();

			// Register this install (token + metadata + subscribed events) on boot.
			useNotificationStore.getState().registerDevice().catch(logError);

			try {
				onForegroundMessage((message) => {
					if (messageIdRef.current !== message.messageId) {
						errorHandler(Translations.alertNewNotification, message.body ?? null);
						messageIdRef.current = message.messageId;
						getNotificationsByToken().catch(logError);
					}
				});
			} catch (error) {
				console.error('Error setting up the push message listener:', error);
			}
		} finally {
			setResourcesLoaded(true);
		}
	}

	return resourcesLoaded;
}

export default useCachedResources;
